import { useState } from 'react';
import { Button } from '@accure/ui/components/button/button-catalyst';
import { InfoIcon, X } from 'lucide-react';

import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

const STORAGE_KEY = 'data-explorer-aggregated-level-banner-dismissed';

/**
 * Uses sessionStorage so the banner returns on the next browser session.
 * This allows users to dismiss it temporarily without losing it forever.
 */
function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function setDismissed(value: boolean): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

/**
 * Determines the display label based on the current data level.
 */
function getLevelLabel(level: string): string {
  switch (level) {
    case 'system':
      return 'Site-level';
    case 'pcs':
      return 'PCS-level';
    default:
      return 'Aggregated-level';
  }
}

export type AggregatedLevelDataBannerProps = {
  /** The current data level being viewed (e.g., 'system', 'pcs', 'container') */
  level: string;
};

/**
 * Informational banner shown when viewing aggregated-level data in Data Explorer.
 * Applies to site-level, PCS-level, and any level that is not the lowest battery level.
 * Explains that this data is shown as received from customer data sources
 * without the same preprocessing applied to the lowest component-level data.
 */
export function AggregatedLevelDataBanner({
  level,
}: AggregatedLevelDataBannerProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(isDismissed);

  if (isHidden) {
    return null;
  }

  function handleDismiss() {
    setDismissed(true);
    setIsHidden(true);
  }

  const levelLabel = getLevelLabel(level);
  const isPcsLevel = level === 'pcs';
  const isSiteLevel = level === 'system';

  return (
    <>
      <Card
        className="mb-5 rounded-xl border bg-muted p-4 text-sm"
        data-testid="aggregated-level-data-banner"
      >
        <div className="flex items-start gap-3">
          <InfoIcon className="text-primary mt-0.5 size-5 shrink-0" />

          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground">
              <strong>{levelLabel} data</strong> shows signals as received from
              your data sources. This data may differ in completeness from the
              lowest component-level views.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              plain
              className="hover:text-primary h-auto whitespace-nowrap px-0 text-xs font-medium text-muted-foreground underline underline-offset-2 transition-colors hover:bg-primary-50"
              onClick={() => setIsDialogOpen(true)}
            >
              Learn more
            </Button>

            <button
              type="button"
              onClick={handleDismiss}
              className="hover:text-primary rounded p-1 text-muted-foreground transition-colors hover:bg-primary-50"
              aria-label="Dismiss banner"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      </Card>

      <Dialog open={isDialogOpen} onClose={setIsDialogOpen}>
        <DialogTitle>About {levelLabel} Data</DialogTitle>
        <DialogDescription>
          Understanding data at different levels in your battery system
        </DialogDescription>

        <DialogBody className="space-y-4 text-sm">
          <section>
            <h4 className="mb-1 font-medium text-gray-900">
              What is {levelLabel.toLowerCase()} data?
            </h4>
            <p className="text-gray-600">
              {isPcsLevel
                ? 'PCS-level data represents signals collected from power conversion systems. This data is displayed as received from your data sources.'
                : isSiteLevel
                  ? 'Site-level data represents signals collected at the overall site or system level, such as ambient temperature, power at the point of interconnection, or aggregate metrics. This data is displayed as received from your data sources.'
                  : 'Aggregated-level data represents signals collected at higher levels in the battery hierarchy, such as container or rack level. This data is displayed as received from your data sources.'}
            </p>
          </section>

          <section>
            <h4 className="mb-1 font-medium text-gray-900">
              How does it differ from component-level data?
            </h4>
            <p className="text-gray-600">
              The lowest component-level data undergoes additional validation
              and preprocessing to ensure consistency. Higher-level signals are
              shown directly as received, which means they can contain flat-line
              periods or values that reflect the original data collection.
            </p>
          </section>

          <section>
            <h4 className="mb-1 font-medium text-gray-900">
              What if the data looks incomplete?
            </h4>
            <p className="text-gray-600">
              If you notice unexpected patterns (such as constant values), this
              typically reflects the upstream data source. Contact your site
              administrator or data provider to investigate and improve data
              quality at the source.
            </p>
          </section>

          {!isPcsLevel && (
            <section>
              <h4 className="mb-1 font-medium text-gray-900">
                How can I see processed data?
              </h4>
              <p className="text-gray-600">
                Select the lowest component level using the filters above to
                view data that has been validated and processed by our analytics
                pipeline.
              </p>
            </section>
          )}
        </DialogBody>

        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
